/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { useTranslation } from "react-i18next";

import SharedStatusBadge from "@/components/SharedStatusBadge/SharedStatusBadge";
import {
  CREDIT_BEHAVIOUR_COLUMNS,
  CreditBehaviourDataRow
} from "@/Pages/Dashboard/LeftSide/types";
import Typography from "@mui/material/Typography";

import { colors } from "@dotnet/telematics-design-system";

interface CreditBehaviourMobileCardProps {
  row: CreditBehaviourDataRow;
  rowIndex: number;
  columns: CREDIT_BEHAVIOUR_COLUMNS[];
}

const CreditBehaviourMobileCard = ({
  row,
  rowIndex,
  columns
}: CreditBehaviourMobileCardProps) => {
  const { t } = useTranslation();

  const isFirstRow = rowIndex === 0;
  const isLastRow = rowIndex === 3;

  const isLastColumnRow = (columnIndex: number) => {
    return columnIndex === columns.length - 1 && isLastRow;
  };

  const isFirstColumnRow = (columnIndex: number) => {
    return columnIndex === 0 && isFirstRow;
  };

  const getRowData = (columnIndex: number) => {
    const sharedStyle = {
      color: colors.textDark
    };

    const { vehicleStatus, device, creditBehaviour } = row;

    if (columnIndex === 0) {
      return (
        <div
          css={css({
            marginTop: "2px"
          })}
        >
          <SharedStatusBadge status={vehicleStatus} showTooltip={false} />
        </div>
      );
    }

    if (columnIndex === 1) {
      return (
        <Typography css={css(sharedStyle)} variant="body2SemiBold">
          {device}
        </Typography>
      );
    }

    return (
      <Typography css={css(sharedStyle)} variant="body2SemiBold">
        {creditBehaviour}
      </Typography>
    );
  };

  return (
    <div>
      {columns.map((column: CREDIT_BEHAVIOUR_COLUMNS, index: number) => (
        <div
          key={`credit_behaviour_mobile_${column}`}
          css={css({
            "& > div + div": {
              marginTop: "8px"
            }
          })}
        >
          <div
            css={css(
              {
                background: colors.white,
                padding: "8px 24px",
                borderTop:
                  index === 0 ? undefined : `1px solid ${colors.gray200}`,

                display: "flex",
                flexDirection: "column"
              },
              isFirstColumnRow(index) && {
                borderTopRightRadius: "8px",
                borderTopLeftRadius: "8px"
              },
              isLastColumnRow(index) && {
                borderBottomRightRadius: "8px",
                borderBottomLeftRadius: "8px"
              }
            )}
          >
            <Typography variant="body4SemiBold" sx={{ color: colors.gray700 }}>
              {t(
                `Dashboard##how credits work##bottom content##table##head##${column}`
              )}
            </Typography>
            {getRowData(index)}
          </div>
        </div>
      ))}
    </div>
  );
};

export default CreditBehaviourMobileCard;
